import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import fontBold from "../../assets/fonts/NotoSansTamil-Bold.ttf";
import fontRegular from "../../assets/fonts/NotoSansTamil-Regular.ttf";

Font.register({
  family: "NotoSansTamil",
  fonts: [
    { src: fontRegular, fontWeight: "normal" },
    { src: fontBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 15,
    backgroundColor: "#fff",
    fontFamily: "NotoSansTamil",
  },
  masterGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
  },
  gridCard: {
    width: "49%",
    height: "240pt",
    margin: "0.5%",
    border: "1px solid #ccc",
    padding: 5,
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    backgroundColor: "#004085",
    color: "#fff",
    padding: 3,
    marginBottom: 2,
  },
  staffName: { fontSize: 10, fontWeight: "bold" },
  categoryName: { fontSize: 8, marginTop: 1 },

  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#bfbfbf",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#bfbfbf",
    minHeight: 14,
    alignItems: "center",
  },
  // --- Adjusted Widths for Separate Columns ---
  cellDate: {
    width: "50pt",
    fontSize: 7,
    padding: 2,
    borderRightWidth: 0.5,
    borderRightColor: "#bfbfbf",
    textAlign: "center",
  },
  cellDay: {
    width: "45pt", // Increased from 30pt to fit "Wednesday"
    fontSize: 7,
    padding: 2,
    borderRightWidth: 0.5,
    borderRightColor: "#bfbfbf",
    textAlign: "center",
  },
  cellProduct: {
    flex: 1,
    fontSize: 7,
    padding: 2,
    borderRightWidth: 0.5,
    borderRightColor: "#bfbfbf",
    textAlign: "center",
  },
  headerCell: { backgroundColor: "#f2f2f2", fontWeight: "bold" },
  footerRow: { backgroundColor: "#f9f9f9" },

  grandTotalSection: {
    marginTop: 10,
    borderTop: "2px solid #000",
    paddingTop: 5,
    textAlign: "right",
  },
});

const PDFReport = ({ data }) => {
  const transformData = (data) => {
    let cardList = [];
    let dateMap = new Map();
    let grandTotal = 0;

    const grouped = {};
    data.forEach((entry) => {
      const d = new Date(entry.entry_date);
      const dateString = d.toLocaleDateString("en-GB");
      const dayString = d.toLocaleDateString("en-GB", { weekday: "long" });

      // Use a combined key for the Map to keep date and day linked
      const dateKey = `${dateString}|${dayString}`;
      dateMap.set(dateKey, d.getTime());

      if (!grouped[entry.staff_name]) grouped[entry.staff_name] = {};
      if (!grouped[entry.staff_name][entry.staff_type]) {
        grouped[entry.staff_name][entry.staff_type] = {
          staffId: entry.staff_id,
          staffName: entry.staff_name,
          category: entry.staff_type,
          products: {},
        };
      }

      entry.products.forEach((p) => {
        if (
          !grouped[entry.staff_name][entry.staff_type].products[p.product_name]
        ) {
          grouped[entry.staff_name][entry.staff_type].products[p.product_name] =
          {
            rate: p.per_cooly_rate,
            total_count: 0,
            total_val: 0,
            counts: {},
          };
        }
        const prodRef =
          grouped[entry.staff_name][entry.staff_type].products[p.product_name];
        prodRef.counts[dateKey] = (prodRef.counts[dateKey] || 0) + p.count;
        prodRef.total_count += p.count;
        prodRef.total_val += p.total;
        grandTotal += p.total;
      });
    });

    const sortedDates = Array.from(dateMap.keys()).sort(
      (a, b) => dateMap.get(a) - dateMap.get(b),
    );

    Object.values(grouped).forEach((staffObj) => {
      Object.values(staffObj).forEach((catObj) => {
        cardList.push(catObj);

      });
    });

    return { cardList, uniqueDates: sortedDates, grandTotal };
  };

  const { cardList, uniqueDates, grandTotal } = transformData(data || []);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text
          style={{
            textAlign: "center",
            fontSize: 12,
            marginBottom: 10,
            fontWeight: "bold",
          }}
        >
          SRI GURULAKSHMI FIREWORKS INDUSTRIES - COOLIE SALARY REPORT
        </Text>

        <View style={styles.masterGrid}>
          {cardList.map((card, index) => {
            const productNames = Object.keys(card.products);
            let catTotal = 0;
            productNames.forEach(
              (p) => (catTotal += card.products[p].total_val),
            );


            const sameStaffCards = cardList.filter(c => c.staffId === card.staffId);

            const staffTotal = sameStaffCards.reduce((sum, c) => {
              return sum + Object.values(c.products)
                .reduce((s, p) => s + p.total_val, 0);
            }, 0);

            // check if current card is last card of that staff
            const staffIndexes = cardList
              .map((c, i) => c.staffId === card.staffId ? i : -1)
              .filter(i => i !== -1);

            const isLastCard = index === staffIndexes[staffIndexes.length - 1];

            return (
              <View key={index} style={styles.gridCard} wrap={false}>
                <View style={styles.cardHeader}>
                  <Text style={styles.staffName}>Staff: {card.staffName}</Text>
                  {/* Update this line to display the numeric ID */}
                  <Text style={{ fontSize: 9, marginTop: 1 }}>
                    Staff Number: {card.staffId}
                  </Text>
                  <Text style={styles.categoryName}>
                    Category: {card.category}
                  </Text>
                </View>

                <View style={styles.table}>
                  {/* Table Header */}
                  <View style={[styles.row, styles.headerCell]}>
                    <Text style={styles.cellDate}>Date</Text>
                    <Text style={styles.cellDay}>Day</Text>
                    {productNames.map((p) => (
                      <Text key={p} style={styles.cellProduct}>
                        {p}
                      </Text>
                    ))}
                  </View>

                  {/* Date Rows */}
                  {uniqueDates.map((dateKey) => {
                    const [dateVal, dayVal] = dateKey.split("|");
                    return (
                      <View key={dateKey} style={styles.row}>
                        <Text style={styles.cellDate}>{dateVal}</Text>
                        <Text style={styles.cellDay}>{dayVal}</Text>
                        {productNames.map((p) => (
                          <Text key={p} style={styles.cellProduct}>
                            {card.products[p].counts[dateKey] || "-"}
                          </Text>
                        ))}
                      </View>
                    );
                  })}

                  {/* Footer Stats - Combined width (50+30) + border adjustment = 80pt */}
                  {/* Footer Stats - Adjusted width to 95pt */}
                  <View style={[styles.row, styles.footerRow]}>
                    <Text
                      style={{
                        width: "95pt",
                        fontSize: 7,
                        padding: 2,
                        borderRightWidth: 0.5,
                        borderRightColor: "#bfbfbf",
                        textAlign: "center",
                      }}
                    >
                      Total Qty
                    </Text>
                    {productNames.map((p) => (
                      <Text key={p} style={styles.cellProduct}>
                        {card.products[p].total_count}
                      </Text>
                    ))}
                  </View>

                  <View
                    style={[
                      styles.row,
                      { borderBottomWidth: 0, backgroundColor: "#e2f0d9" },
                    ]}
                  >
                    <Text
                      style={{
                        width: "95pt",
                        fontSize: 7,
                        padding: 2,
                        borderRightWidth: 0.5,
                        borderRightColor: "#bfbfbf",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      Coolie (₹)
                    </Text>
                    {productNames.map((p) => (
                      <Text
                        key={p}
                        style={[styles.cellProduct, { fontWeight: "bold" }]}
                      >
                        {card.products[p].total_val.toFixed(2)}
                      </Text>
                    ))}
                  </View>
                </View>

                <Text
                  style={{
                    fontSize: 8,
                    textAlign: "right",
                    marginTop: "auto",
                    fontWeight: "bold",
                  }}
                >
                  Sub-Total: ₹{catTotal.toFixed(2)}
                </Text>
                {isLastCard && (
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "right",
                      fontWeight: "bold",
                      marginTop: 2,
                    }}
                  >
                    Grand Total: ₹{staffTotal.toFixed(2)}
                  </Text>
                )}
              </View>
            );
          })}

        </View>

        <View style={styles.grandTotalSection}>
          <Text style={{ fontSize: 12, fontWeight: "bold" }}>
            OVERALL GRAND TOTAL: ₹ {grandTotal.toFixed(2)}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFReport;
